import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useGetDocImagePresignedURL } from '@/api/queries/useGetDocImagePresignedURL';
import { Icon } from '@/new/components/icon';
import { Location } from '@/types';

const MAX_DISPLAY_WIDTH = 1200;
const PADDING_RATIO = 0.1;
const HIGHLIGHT_OPACITY = 0.15;

interface FactSourceImageProps {
  location: Location;
  showOpenButton?: boolean;
  currentSourceIndex?: number;
  totalSources?: number;
  onPrevSource?: () => void;
  onNextSource?: () => void;
}

export const FactSourceImage = ({
  location,
  showOpenButton = false,
  currentSourceIndex,
  totalSources,
  onPrevSource,
  onNextSource,
}: FactSourceImageProps) => {
  const { matterId, subsetId } = useParams();
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const firstBoundingBox = location.boundingBoxes?.[0];
  const pageNumber = firstBoundingBox?.pageNumber;

  const cropArea = useMemo(() => {
    if (!firstBoundingBox || !location.boundingBoxes) return null;

    const pageBoxes = location.boundingBoxes.filter((box) => box.pageNumber === pageNumber);

    if (pageBoxes.length === 0) return null;

    // Calculate the union of all bounding boxes to determine crop area
    const minLeft = Math.min(...pageBoxes.map((box) => box.left));
    const minTop = Math.min(...pageBoxes.map((box) => box.top));
    const maxRight = Math.max(...pageBoxes.map((box) => box.left + box.width));
    const maxBottom = Math.max(...pageBoxes.map((box) => box.top + box.height));

    // Add padding around the crop area
    const cropWidth = maxRight - minLeft;
    const cropHeight = maxBottom - minTop;
    const paddingX = cropWidth * PADDING_RATIO;
    const paddingY = cropHeight * PADDING_RATIO;

    const cropLeft = Math.max(0, minLeft - paddingX);
    const cropTop = Math.max(0, minTop - paddingY);
    const cropRight = Math.min(1, maxRight + paddingX);
    const cropBottom = Math.min(1, maxBottom + paddingY);

    return {
      cropLeft,
      cropTop,
      cropRight,
      cropBottom,
      pageBoxes,
    };
  }, [firstBoundingBox, location.boundingBoxes, pageNumber]);

  const { data: imageUrl, isLoading: imageLoading } = useGetDocImagePresignedURL(
    matterId,
    location.docId,
    pageNumber !== undefined ? pageNumber - 1 : 0,
  );

  useEffect(() => {
    if (!imageUrl || !cropArea) {
      setIsLoading(false);
      return;
    }

    const cropAndHighlightImage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          setError('Failed to get canvas context');
          setIsLoading(false);
          return;
        }

        const { cropLeft, cropTop, cropRight, cropBottom, pageBoxes } = cropArea;

        // Convert normalized coordinates to pixel coordinates
        const sourceX = cropLeft * img.width;
        const sourceY = cropTop * img.height;
        const sourceWidth = (cropRight - cropLeft) * img.width;
        const sourceHeight = (cropBottom - cropTop) * img.height;

        // Set canvas size to the cropped area
        const aspectRatio = sourceHeight / sourceWidth;
        const displayWidth = Math.min(MAX_DISPLAY_WIDTH, sourceWidth);
        const displayHeight = displayWidth * aspectRatio;

        const dpr = window.devicePixelRatio ?? 1;
        const canvasWidth = displayWidth * dpr;
        const canvasHeight = displayHeight * dpr;

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, displayWidth, displayHeight);

        // Bounding box highlighting
        pageBoxes.forEach((box) => {
          const relativeLeft = (box.left - cropLeft) / (cropRight - cropLeft);
          const relativeTop = (box.top - cropTop) / (cropBottom - cropTop);
          const relativeWidth = box.width / (cropRight - cropLeft);
          const relativeHeight = box.height / (cropBottom - cropTop);

          const highlightX = relativeLeft * displayWidth;
          const highlightY = relativeTop * displayHeight;
          const highlightWidth = relativeWidth * displayWidth;
          const highlightHeight = relativeHeight * displayHeight;

          ctx.fillStyle = `rgba(255, 255, 0, ${HIGHLIGHT_OPACITY})`;
          ctx.fillRect(highlightX, highlightY, highlightWidth, highlightHeight);
        });

        const dataUrl = canvas.toDataURL('image/png');
        setCroppedImageUrl(dataUrl);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to process image');
        setIsLoading(false);
      }
    };

    cropAndHighlightImage();
  }, [imageUrl, cropArea]);

  const handleOpenInNewWindow = () => {
    if (location.doc && matterId && subsetId) {
      const documentUrl = `/matters/${matterId}/subsets/${subsetId}/docs/${location.doc.id}`;
      window.open(documentUrl, '_blank');
    }
  };

  if (!firstBoundingBox) {
    return <div className="text-xs text-gray-500 italic">No source location available</div>;
  }

  if (imageLoading || isLoading) {
    return (
      <div className="flex h-24 items-center justify-center rounded bg-gray-100">
        <div className="text-xs text-gray-500">Loading source image...</div>
      </div>
    );
  }

  if (error) {
    return <div className="text-xs text-red-500 italic">Error loading source: {error}</div>;
  }

  return (
    <div>
      {croppedImageUrl ? (
        <div className="overflow-hidden rounded border border-gray-200">
          <img src={croppedImageUrl} alt="Fact source" className="h-auto w-full" />
          <div className="flex items-center justify-between bg-gray-50 px-2 py-1 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              {totalSources && totalSources > 1 && onPrevSource && onNextSource && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={onPrevSource}
                    className="flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-gray-100"
                    disabled={totalSources <= 1}
                  >
                    <Icon className="lucide--chevron-left" />
                  </button>

                  <span className="text-xs font-medium">
                    Source {(currentSourceIndex || 0) + 1} / {totalSources}
                  </span>

                  <button
                    onClick={onNextSource}
                    className="flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-gray-100"
                    disabled={totalSources <= 1}
                  >
                    <Icon className="lucide--chevron-right" />
                  </button>
                </div>
              )}

              <span>
                {location.doc?.file?.name || 'Unknown document'} - Page {pageNumber || 1}
              </span>
            </div>

            {showOpenButton && (
              <button
                onClick={handleOpenInNewWindow}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
              >
                <Icon className="lucide--external-link" />
                Open in new window
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
          <div className="mb-2 flex items-center justify-center text-gray-400">
            <Icon className="lucide--file-image" />
          </div>
          <p className="text-sm font-medium text-gray-600">Cannot display document snippet</p>
          <p className="mt-1 text-xs text-gray-500">Please view the full document to see the source</p>
          {showOpenButton && (
            <button
              onClick={handleOpenInNewWindow}
              className="mt-3 inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
            >
              <Icon className="lucide--external-link" />
              Open document
            </button>
          )}
        </div>
      )}
    </div>
  );
};
